package dev.lingvo.site.pages.app.account

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * Closing your account, on its own route because for an owner it is a multi-step resolution.
 *
 * Every state is computed live rather than described in prose that goes stale: which workspaces you
 * are the only owner of, which you merely belong to, how many tokens die with you.
 *
 * **"Your translations stay; your name comes off them."** That is what `ON DELETE SET NULL` actually
 * does, and saying it plainly beats implying that everything vanishes — which would be both wrong
 * and, for somebody's colleagues, alarming.
 *
 * Sole ownership is resolved first: the button stays **disabled with the reason beside it**, never a
 * click that then explains.
 */
@Page("/app/account/close")
@Layout(".components.layouts.AppLayout")
@Composable
fun CloseAccountPage() {
    val scope = rememberCoroutineScope()
    var refreshKey by remember { mutableStateOf(0) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }

    val state = loadAsync(refreshKey) { ApiClient.closeAccountPreview() }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.account.close.close_your_account", "Close your account"), t("app.account.close.what_happens_before_anything_happens", "What happens, before anything happens."))

        // `live`: this appears only because an action the reader just took failed, and it appears
        // above the control they are still on. A warning that arrives silently is one a
        // keyboard reader never learns about, and they will press the button again.
        message?.let {
            LAlert(
                t("app.account.close.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        LAsyncContent(
            state = state,
            loadingLabel = t(
                "app.account.close.working_out_what_this_would_do",
                "Working out what this would do",
            ),
            subject = t("app.account.close.what_closing_your_account_would_do", "what closing your account would do"),
            onRetry = { refreshKey++ },
        ) { preview ->
            if (preview.pending) {
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.gap(Space.MD)) {
                        LAlert(
                            tf(
                                "app.account.close.closes_on",
                                "Your account closes on {date}",
                                "date" to preview.scheduledFor?.take(10),
                            ),
                            t("app.account.close.nothing_has_been_deleted_yet_cancelling", "Nothing has been deleted yet. Cancelling costs one click and leaves " +
                                "everything exactly as it is."),
                            variant = LAlertVariant.WARNING,
                        )
                        LButton(
                            t("app.account.close.keep_my_account", "Keep my account"),
                            {
                                scope.launch {
                                    busy = true
                                    message = runCatching { ApiClient.cancelAccountClose() }
                                        .fold({ refreshKey++; null }, { it.message })
                                    busy = false
                                }
                            },
                            enabled = !busy,
                            loading = busy,
                        )
                    }
                }
                return@LAsyncContent
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.account.close.what_happens", "What happens"))
                    Ul {
                        Fact(t("app.account.close.nothing_is_deleted_for_seven_days", "Nothing is deleted for seven days. Until then one click brings it all back."))
                        // Honest about ON DELETE SET NULL rather than implying everything goes.
                        Fact(t("app.account.close.your_translations_stay_your_name_comes", "Your translations stay. Your name comes off them."))
                        Fact(
                            tPlural(
                                "app.account.close.api_tokens_stop",
                                preview.apiTokens,
                                "Your {count} API token stops working immediately on deletion.",
                                "Your {count} API tokens stop working immediately on deletion.",
                            ),
                        )
                        if (preview.memberOf.isNotEmpty()) {
                            Fact(
                                tf(
                                    "app.account.close.you_leave_workspaces",
                                    "You leave {workspaces}. Nobody else is affected.",
                                    "workspaces" to preview.memberOf.joinToString(", "),
                                ),
                            )
                        }
                    }

                    if (preview.soleOwnerOf.isNotEmpty()) {
                        LAlert(
                            t("app.account.close.somebody_else_has_to_own_these", "Somebody else has to own these first"),
                            tf(
                                "app.account.close.you_are_the_only_owner_of_projects",
                                "You are the only owner of {projects}. Make another member an owner, " +
                                    "or delete the workspace, and then come back here.",
                                "projects" to preview.soleOwnerOf.joinToString(", "),
                            ),
                            variant = LAlertVariant.WARNING,
                        )
                    }

                    Row(Modifier.gap(Space.SM)) {
                        LButton(
                            t("app.account.close.close_my_account", "Close my account"),
                            {
                                scope.launch {
                                    busy = true
                                    message = runCatching { ApiClient.closeAccount() }
                                        .fold({ refreshKey++; null }, { it.message })
                                    busy = false
                                }
                            },
                            // Disabled with the reason visible above, rather than a click that
                            // then explains.
                            enabled = preview.soleOwnerOf.isEmpty() && !busy,
                            loading = busy,
                            variant = LButtonVariant.SECONDARY,
                        )
                    }
                    LMuted(
                        t("app.account.close.cancelling_a_subscription_is_a_different_2", "Cancelling a subscription is a different thing and does not close your " +
                            "account."),
                    )
                }
            }
        }
    }
}

@Composable
private fun Fact(text: String) {
    Li { Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) { Text(text) } }
}

